import { createAsyncThunk, createSlice, isAnyOf, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit'

import {
  addServiceOrderDocument,
  archiveServiceOrder,
  assignServiceOrder,
  cancelServiceOrder,
  createServiceOrder,
  getServiceOrderDeviceDocuments,
  getServiceOrderDevices,
  getServiceOrders,
  getServiceTechnicians,
  moveServiceOrder,
  removeServiceOrderDocument,
  updateServiceOrder,
  type ServiceOrderCreateRequest,
  type ServiceOrderDeviceDocumentDto,
  type ServiceOrderDeviceDto,
  type ServiceOrderDocumentRequest,
  type ServiceOrderDto,
  type ServiceOrderSummaryDto,
  type ServiceOrderUpdateRequest,
  type ServiceTechnicianDto,
} from '@/api/service-order.api'
import { getServiceArchive, type ServiceArchiveEntryDto } from '@/api/service-archive.api'
import { resetStore } from '@/store/actions/reset'
import { createUserData, deleteUserData, updateUserData } from '@/store/slices/users-slice'
import { Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'

interface ServiceOrdersState {
  orders: ServiceOrderSummaryDto[]
  status: Status
  requestId: string | null
  technicians: ServiceTechnicianDto[]
  techniciansStatus: Status
  /** Zmiana konta unieważnia trwające pobranie serwisantów — jego odpowiedź sprzed zmiany nie może zostać zapisana. */
  techniciansRequestId: string | null
  devices: ServiceOrderDeviceDto[]
  devicesStatus: Status
  /** Lista urządzeń wraca przy każdym otwarciu formularza — starsza odpowiedź nie może nadpisać nowszej. */
  devicesRequestId: string | null
  saveStatus: Status
  /** Przesunięcia kart idą pojedynczo — kolejne czeka, aż serwer zapisze poprzednie. */
  moveStatus: Status
  /** Mutacja unieważniła trwające pobranie listy — jego odpowiedź sprzed zmiany zostanie pominięta. */
  listInvalidated: boolean
  /** Liczba trwających mutacji — ponowne pobranie dopiero po ostatniej. */
  pendingMutations: number
  /** Lista do ponownego pobrania po zakończonej mutacji, której wynik unieważnił poprzednie pobranie. */
  stale: boolean
}

const initialState: ServiceOrdersState = {
  orders: [],
  status: 'idle',
  requestId: null,
  technicians: [],
  techniciansStatus: 'idle',
  techniciansRequestId: null,
  devices: [],
  devicesStatus: 'idle',
  devicesRequestId: null,
  saveStatus: 'idle',
  moveStatus: 'idle',
  listInvalidated: false,
  pendingMutations: 0,
  stale: false,
}

export const fetchServiceOrders = createAsyncThunk<ServiceOrderSummaryDto[]>(
  'serviceOrders/fetch',
  async (_, { rejectWithValue }) => {
    try {
      return await getServiceOrders()
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const fetchServiceTechnicians = createAsyncThunk<ServiceTechnicianDto[]>(
  'serviceOrders/fetchTechnicians',
  async (_, { rejectWithValue }) => {
    try {
      return await getServiceTechnicians()
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const fetchServiceOrderDevices = createAsyncThunk<ServiceOrderDeviceDto[]>(
  'serviceOrders/fetchDevices',
  async (_, { rejectWithValue }) => {
    try {
      return await getServiceOrderDevices()
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

/** Lista nazw zmienia się z wybranym urządzeniem formularza, więc nie trafia do stanu. */
export const fetchServiceOrderDeviceDocuments = createAsyncThunk<
  ServiceOrderDeviceDocumentDto[],
  { deviceId: string; language?: string }
>('serviceOrders/fetchDeviceDocuments', async ({ deviceId, language }, { rejectWithValue }) => {
  try {
    return await getServiceOrderDeviceDocuments(deviceId, language)
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

/** Największa strona archiwum, jaką oddaje backend. */
const ARCHIVE_PAGE_LIMIT = 100

/**
 * Zarchiwizowane zlecenia do pola „Zlecenia powiązane” — opcje formularza, więc nie trafiają do stanu
 * (stan archiwum należy do jego tabeli). Archiwum miesza zlecenia z odrzuconymi zgłoszeniami.
 */
export const fetchArchivedServiceOrders = createAsyncThunk<ServiceArchiveEntryDto[]>(
  'serviceOrders/fetchArchived',
  async (_, { rejectWithValue }) => {
    try {
      const orders: ServiceArchiveEntryDto[] = []
      let page = 0
      let total = 0
      do {
        page += 1
        const result = await getServiceArchive({ page, limit: ARCHIVE_PAGE_LIMIT })
        orders.push(...result.data.filter((entry) => entry.source === 'ORDER'))
        total = result.total
        if (result.data.length === 0) break
      } while (page * ARCHIVE_PAGE_LIMIT < total)
      return orders
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const moveServiceOrderCard = createAsyncThunk<void, { id: string; beforeId: string | null }>(
  'serviceOrders/move',
  async ({ id, beforeId }, { rejectWithValue }) => {
    try {
      return await moveServiceOrder(id, beforeId)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const assignServiceOrderTechnician = createAsyncThunk<
  ServiceOrderDto,
  { id: string; assigneeId: string | null }
>('serviceOrders/assign', async ({ id, assigneeId }, { rejectWithValue }) => {
  try {
    return await assignServiceOrder(id, assigneeId)
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

export const cancelServiceOrderThunk = createAsyncThunk<ServiceOrderDto, { id: string; reason: string }>(
  'serviceOrders/cancel',
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      return await cancelServiceOrder(id, reason)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const archiveServiceOrderThunk = createAsyncThunk<ServiceOrderDto, string>(
  'serviceOrders/archive',
  async (id, { rejectWithValue }) => {
    try {
      return await archiveServiceOrder(id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createServiceOrderThunk = createAsyncThunk<ServiceOrderDto, ServiceOrderCreateRequest>(
  'serviceOrders/create',
  async (payload, { rejectWithValue }) => {
    try {
      return await createServiceOrder(payload)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const updateServiceOrderThunk = createAsyncThunk<
  ServiceOrderDto,
  { id: string; payload: ServiceOrderUpdateRequest }
>('serviceOrders/update', async ({ id, payload }, { rejectWithValue }) => {
  try {
    return await updateServiceOrder(id, payload)
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

export const addServiceOrderDocumentThunk = createAsyncThunk<
  ServiceOrderDto,
  { id: string; payload: ServiceOrderDocumentRequest }
>('serviceOrders/addDocument', async ({ id, payload }, { rejectWithValue }) => {
  try {
    return await addServiceOrderDocument(id, payload)
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

export const removeServiceOrderDocumentThunk = createAsyncThunk<void, { id: string; documentId: string }>(
  'serviceOrders/removeDocument',
  async ({ id, documentId }, { rejectWithValue }) => {
    try {
      return await removeServiceOrderDocument(id, documentId)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

const replaceOrder = (state: ServiceOrdersState, order: ServiceOrderDto) => {
  const index = state.orders.findIndex((entry) => entry.id === order.id)
  if (index === -1) state.orders.unshift(order)
  else state.orders[index] = order
}

const removeOrder = (state: ServiceOrdersState, id: string) => {
  state.orders = state.orders.filter((entry) => entry.id !== id)
}

const MUTATIONS = [
  moveServiceOrderCard,
  assignServiceOrderTechnician,
  cancelServiceOrderThunk,
  archiveServiceOrderThunk,
  createServiceOrderThunk,
  updateServiceOrderThunk,
  addServiceOrderDocumentThunk,
  removeServiceOrderDocumentThunk,
] as const

const serviceOrdersSlice = createSlice({
  name: 'serviceOrders',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchServiceOrders.pending, (state, action) => {
        state.status = 'loading'
        state.stale = false
        // Pobranie zaczęte w trakcie mutacji może odczytać bazę sprzed jej zapisu — odpowiedź jest pomijana,
        // a lista wraca po ostatniej mutacji.
        if (state.pendingMutations > 0) {
          state.requestId = null
          state.listInvalidated = true
          return
        }
        state.requestId = action.meta.requestId
      })
      // Lista wraca też po każdej zmianie kolejności — starsza odpowiedź nie może nadpisać nowszej.
      .addCase(fetchServiceOrders.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.status = 'succeeded'
        state.orders = action.payload
      })
      .addCase(fetchServiceOrders.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.status = 'failed'
      })
      .addCase(fetchServiceTechnicians.pending, (state, action) => {
        state.techniciansRequestId = action.meta.requestId
        state.techniciansStatus = 'loading'
      })
      .addCase(fetchServiceTechnicians.fulfilled, (state, action) => {
        if (state.techniciansRequestId !== action.meta.requestId) return
        state.techniciansStatus = 'succeeded'
        state.technicians = action.payload
      })
      .addCase(fetchServiceTechnicians.rejected, (state, action) => {
        if (state.techniciansRequestId !== action.meta.requestId) return
        state.techniciansStatus = 'failed'
      })
      .addCase(fetchServiceOrderDevices.pending, (state, action) => {
        state.devicesRequestId = action.meta.requestId
        state.devicesStatus = 'loading'
      })
      .addCase(fetchServiceOrderDevices.fulfilled, (state, action) => {
        if (state.devicesRequestId !== action.meta.requestId) return
        state.devicesStatus = 'succeeded'
        state.devices = action.payload
      })
      .addCase(fetchServiceOrderDevices.rejected, (state, action) => {
        if (state.devicesRequestId !== action.meta.requestId) return
        state.devicesStatus = 'failed'
      })
      // Karta przeskakuje od razu; po odmowie ekran pobiera listę ponownie i wraca do kolejności z bazy.
      .addCase(moveServiceOrderCard.fulfilled, (state) => {
        state.moveStatus = 'succeeded'
      })
      .addCase(moveServiceOrderCard.rejected, (state) => {
        state.moveStatus = 'failed'
      })
      .addCase(moveServiceOrderCard.pending, (state, action) => {
        state.moveStatus = 'loading'
        const { id, beforeId } = action.meta.arg
        const moved = state.orders.find((entry) => entry.id === id)
        if (!moved || id === beforeId) return
        const rest = state.orders.filter((entry) => entry.id !== id)
        const at = beforeId ? rest.findIndex((entry) => entry.id === beforeId) : -1
        if (at === -1) {
          const lastInColumn = rest.reduce((last, entry, index) => (entry.status === moved.status ? index : last), -1)
          rest.splice(lastInColumn + 1, 0, moved)
        } else {
          rest.splice(at, 0, moved)
        }
        state.orders = rest
      })
      .addCase(assignServiceOrderTechnician.fulfilled, (state, action) => {
        replaceOrder(state, action.payload)
      })
      .addCase(cancelServiceOrderThunk.fulfilled, (state, action) => {
        removeOrder(state, action.payload.id)
      })
      .addCase(archiveServiceOrderThunk.fulfilled, (state, action) => {
        removeOrder(state, action.payload.id)
      })
      .addCase(createServiceOrderThunk.pending, (state) => {
        state.saveStatus = 'loading'
      })
      .addCase(createServiceOrderThunk.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded'
        replaceOrder(state, action.payload)
      })
      .addCase(createServiceOrderThunk.rejected, (state) => {
        state.saveStatus = 'failed'
      })
      .addCase(updateServiceOrderThunk.pending, (state) => {
        state.saveStatus = 'loading'
      })
      .addCase(updateServiceOrderThunk.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded'
        replaceOrder(state, action.payload)
      })
      .addCase(updateServiceOrderThunk.rejected, (state) => {
        state.saveStatus = 'failed'
      })
      .addCase(addServiceOrderDocumentThunk.fulfilled, (state, action) => {
        replaceOrder(state, action.payload)
      })
      .addCase(resetStore, () => initialState)
      // Lista pobrana przed mutacją nadpisałaby jej wynik stanem sprzed zmiany — trwające pobranie jest pomijane,
      // a po zakończeniu mutacji ekran pobiera listę raz jeszcze, już ze zmianą zapisaną w bazie.
      .addMatcher(isPending(...MUTATIONS), (state) => {
        state.pendingMutations += 1
        if (state.status !== 'loading' || state.requestId === null) return
        state.requestId = null
        state.listInvalidated = true
      })
      .addMatcher(isAnyOf(isFulfilled(...MUTATIONS), isRejected(...MUTATIONS)), (state) => {
        state.pendingMutations = Math.max(0, state.pendingMutations - 1)
        if (state.pendingMutations > 0 || !state.listInvalidated) return
        state.listInvalidated = false
        state.stale = true
      })
      // Utworzenie, edycja (rola, status) lub usunięcie konta zmienia skład listy serwisantów — lista wraca do
      // pobrania przy następnym wejściu na tablicę albo otwarciu formularza, bez przeładowania strony.
      // Pobranie trwające w chwili zapisu mogło odczytać bazę sprzed zmiany, więc jego odpowiedź jest pomijana.
      .addMatcher(isFulfilled(createUserData, updateUserData, deleteUserData), (state) => {
        state.techniciansRequestId = null
        state.techniciansStatus = 'idle'
      })
  },
})

export default serviceOrdersSlice.reducer
